/**
 * BroadcastChannel wrapper for cross-tab state sync.
 *
 * Used to keep cart, wishlist, and auth state in sync across tabs of the
 * same browser without going through the server. Other tabs get the
 * update in <50ms.
 *
 * Channel layout:
 *   'jns:cart'      — full cart array on every mutation
 *   'jns:wishlist'  — array of item ids on every mutation
 *   'jns:auth'      — { type: 'login' | 'logout', user? }
 *
 * Falls back to localStorage 'storage' event on browsers without
 * BroadcastChannel (very old Safari). Same window doesn't receive its
 * own broadcasts.
 */
const channels = new Map();   // name → BroadcastChannel instance

function _channel(name) {
    if (typeof BroadcastChannel === "undefined") return null;
    let ch = channels.get(name);
    if (!ch) {
        ch = new BroadcastChannel(name);
        channels.set(name, ch);
    }
    return ch;
}

export function bcEmit(name, payload) {
    const ch = _channel(name);
    if (ch) {
        try { ch.postMessage(payload); } catch { /* DataCloneError on unserializable — ignore */ }
        return;
    }
    // Fallback for unsupported browsers — bump a localStorage key that
    // other tabs listen for via 'storage' events.
    try {
        const key = `__bc_fallback__:${name}`;
        localStorage.setItem(key, JSON.stringify({ t: Date.now(), payload }));
        localStorage.removeItem(key);
    } catch { /* private mode / quota — ignore */ }
}

export function bcSubscribe(name, handler) {
    const ch = _channel(name);
    if (ch) {
        const wrapped = (e) => handler(e.data);
        ch.addEventListener("message", wrapped);
        return () => ch.removeEventListener("message", wrapped);
    }
    // Fallback path
    const key = `__bc_fallback__:${name}`;
    const wrapped = (e) => {
        if (e.key !== key || !e.newValue) return;
        try { handler(JSON.parse(e.newValue).payload); } catch { /* ignore */ }
    };
    window.addEventListener("storage", wrapped);
    return () => window.removeEventListener("storage", wrapped);
}

// Named channels — keep magic strings out of the consumers
export const CHAN_CART = "jns:cart";
export const CHAN_WISHLIST = "jns:wishlist";
export const CHAN_AUTH = "jns:auth";
